'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeftRight } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { swapTeamsBetweenGroups } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import type { Group, Team } from '@/types/tournament.model';
import { getTeamName } from '@/utils/team-utils';
import { groupLabel, hasTeamPlayedInGroup } from '@/utils/fixture-utils';
import { SwapStepsFooter } from './SwapStepsFooter';
import { SwapTeamSelectButton, SwapTeamSummaryBlock } from './SwapTeamOption';

type TeamSelection = {
  groupIndex: number;
  teamId: string;
  teamName: string;
};

interface SwapTeamsBetweenGroupsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  /** Tournament sport UID (used to pick the correct ranking for ELO). */
  tournamentSportUid?: string;
  groups: Group[];
  /** With an active fixture players already see their groups, so they get notified. */
  isFixtureActive: boolean;
  onSuccess: () => void;
}

function findTeamInGroups(groups: Group[], teamId: string): Team | undefined {
  for (const g of groups) {
    const item = g.teamList.find((t) => t.team.id === teamId);
    if (item) return item.team;
  }
  return undefined;
}

export function SwapTeamsBetweenGroupsModal({
  open,
  onOpenChange,
  tournamentId,
  tournamentSportUid,
  groups,
  isFixtureActive,
  onSuccess,
}: SwapTeamsBetweenGroupsModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [teamA, setTeamA] = useState<TeamSelection | null>(null);
  const [teamB, setTeamB] = useState<TeamSelection | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setTeamA(null);
      setTeamB(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!teamA || !teamB) return;
    try {
      setIsSubmitting(true);
      await swapTeamsBetweenGroups(tournamentId, teamA.teamId, teamB.teamId);
      toast({
        title: 'Intercambio realizado',
        description: 'El fixture se actualizó correctamente.',
        variant: 'default',
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      logError('SwapTeamsBetweenGroupsModal.handleConfirm', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo intercambiar los equipos. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const playedTeamIds = useMemo(
    () =>
      new Set(
        groups.flatMap((g) =>
          g.teamList
            .filter(({ team }) => hasTeamPlayedInGroup(g, team.id))
            .map(({ team }) => team.id),
        ),
      ),
    [groups],
  );
  // The caller only mounts the modal with 2+ teams, so the only way to fall short is teams that already played.
  const canSwap =
    groups.flatMap((g) => g.teamList).length - playedTeamIds.size >= 2;

  const isSameGroupSwap =
    !!teamA && !!teamB && teamA.groupIndex === teamB.groupIndex;

  const teamAObj = teamA ? findTeamInGroups(groups, teamA.teamId) : undefined;
  const teamBObj = teamB ? findTeamInGroups(groups, teamB.teamId) : undefined;

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Intercambiar equipos
          </DialogTitle>
          <DialogDescription>
            Elegí dos equipos: si están en grupos distintos se cambian de
            grupo, y si están en el mismo grupo intercambian sus partidos y
            horarios.
            {isFixtureActive &&
              ' Con el fixture activo solo se pueden mover los equipos que todavía no jugaron, y a los jugadores de los dos equipos les llega un aviso.'}
          </DialogDescription>
        </DialogHeader>

        {!canSwap ? (
          <p className="py-4 text-sm text-muted-foreground">
            Quedan menos de dos equipos que todavía no jugaron, así que no hay
            nada para intercambiar.
          </p>
        ) : (
          <div className="max-h-[min(420px,55vh)] space-y-4 overflow-y-auto pr-1">
            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">
                  1. Elegí el primer equipo
                </p>
                {groups.map((group, groupIndex) => (
                  <div
                    key={groupIndex}
                    className="rounded-lg border bg-muted/30 p-3"
                  >
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {groupLabel(groupIndex)}
                    </p>
                    <ul className="space-y-2">
                      {group.teamList.map(({ team }) => {
                        const name = getTeamName(team);
                        return (
                          <li key={team.id}>
                            <SwapTeamSelectButton
                              team={team}
                              selected={teamA?.teamId === team.id}
                              tournamentSportUid={tournamentSportUid}
                              hasPlayed={playedTeamIds.has(team.id)}
                              onSelect={() =>
                                setTeamA({
                                  groupIndex,
                                  teamId: team.id,
                                  teamName: name,
                                })
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {step === 2 && teamA && (
              <div className="space-y-3">
                <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">Seleccionado: </span>
                  <span className="font-medium">{teamA.teamName}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    ({groupLabel(teamA.groupIndex)})
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground">
                  2. Elegí el otro equipo
                </p>
                {groups.map((group, groupIndex) => {
                  const candidates = group.teamList.filter(
                    ({ team }) => team.id !== teamA.teamId,
                  );
                  if (candidates.length === 0) return null;
                  return (
                    <div
                      key={groupIndex}
                      className="rounded-lg border bg-muted/30 p-3"
                    >
                      <p className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {groupLabel(groupIndex)}
                        {groupIndex === teamA.groupIndex && (
                          <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal">
                            mismo grupo · intercambian partidos y horarios
                          </span>
                        )}
                      </p>
                      <ul className="space-y-2">
                        {candidates.map(({ team }) => {
                          const name = getTeamName(team);
                          return (
                            <li key={team.id}>
                              <SwapTeamSelectButton
                                team={team}
                                selected={teamB?.teamId === team.id}
                                tournamentSportUid={tournamentSportUid}
                                hasPlayed={playedTeamIds.has(team.id)}
                                onSelect={() =>
                                  setTeamB({
                                    groupIndex,
                                    teamId: team.id,
                                    teamName: name,
                                  })
                                }
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            {step === 3 && teamA && teamB && teamAObj && teamBObj && (
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-sm font-medium">Confirmar intercambio</p>
                <p className="text-sm text-muted-foreground">
                  {isSameGroupSwap
                    ? `Intercambiarán sus partidos y horarios dentro del ${groupLabel(
                        teamA.groupIndex,
                      )}:`
                    : 'Se intercambiarán de grupo:'}
                </p>
                <div className="space-y-3">
                  <SwapTeamSummaryBlock
                    team={teamAObj}
                    label={
                      isSameGroupSwap ? undefined : groupLabel(teamA.groupIndex)
                    }
                    tournamentSportUid={tournamentSportUid}
                  />
                  <div className="flex justify-center text-muted-foreground">
                    <ArrowLeftRight className="h-4 w-4" />
                  </div>
                  <SwapTeamSummaryBlock
                    team={teamBObj}
                    label={
                      isSameGroupSwap ? undefined : groupLabel(teamB.groupIndex)
                    }
                    tournamentSportUid={tournamentSportUid}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <SwapStepsFooter
          step={step}
          canSwap={canSwap}
          isSubmitting={isSubmitting}
          nextDisabled={step === 1 ? !teamA : !teamB}
          confirmDisabled={!teamAObj || !teamBObj}
          onCancel={handleClose}
          onBack={() => {
            if (step === 3) return setStep(2);
            setTeamB(null);
            setStep(1);
          }}
          onNext={() => setStep(step === 1 ? 2 : 3)}
          onConfirm={handleConfirm}
        />
      </DialogContent>
    </Dialog>
  );
}
